import { useCallback, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
  getFacebookConversations,
  getFacebookMessages,
  getInstagramConversations,
  getInstagramMessages,
} from 'features/chat/chatActions';
import {
  selectFacebookConversations,
  selectFacebookConversationsLoading,
  selectFacebookMessages,
  selectFacebookMessagesLoading,
  selectFacebookMessagesPagination,
  selectFacebookMessagesUserId,
  selectFacebookPagination,
  selectInstagramConversations,
  selectInstagramConversationsLoading,
  selectInstagramMessages,
  selectInstagramMessagesLoading,
  selectInstagramMessagesPagination,
  selectInstagramMessagesUserId,
  selectInstagramPagination,
} from 'features/chat/chatSlice';

export const useFacebookMessagesInfiniteScroll = (userId, limit = 20) => {
  const dispatch = useDispatch();
  const messages = useSelector(selectFacebookMessages);
  const loading = useSelector(selectFacebookMessagesLoading);
  const pagination = useSelector(selectFacebookMessagesPagination);
  const currentUserId = useSelector(selectFacebookMessagesUserId);

  const isFetchingRef = useRef(false);
  const hasInitialLoadRef = useRef(false);
  const userIdRef = useRef(userId);

  useEffect(() => {
    if (userIdRef.current !== userId) {
      userIdRef.current = userId;
      hasInitialLoadRef.current = false;
    }

    if (userId && !hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      isFetchingRef.current = true;
      dispatch(
        getFacebookMessages({
          userId,
          limit,
          direction: 'initial',
        })
      ).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, userId, limit]);

  const loadOlder = useCallback(() => {
    if (
      !loading &&
      !isFetchingRef.current &&
      pagination.hasNextPage &&
      pagination.cursors.after &&
      currentUserId === userId
    ) {
      isFetchingRef.current = true;
      dispatch(
        getFacebookMessages({
          userId,
          limit,
          after: pagination.cursors.after,
          direction: 'prepend',
        })
      ).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, loading, pagination, userId, currentUserId, limit]);

  const loadNewer = useCallback(() => {
    if (
      !loading &&
      !isFetchingRef.current &&
      pagination.hasPreviousPage &&
      pagination.cursors.before &&
      currentUserId === userId
    ) {
      isFetchingRef.current = true;
      dispatch(
        getFacebookMessages({
          userId,
          limit,
          before: pagination.cursors.before,
          direction: 'prepend',
        })
      ).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, loading, pagination, userId, currentUserId, limit]);

  const refresh = useCallback(() => {
    if (userId) {
      isFetchingRef.current = true;
      hasInitialLoadRef.current = true;
      dispatch(
        getFacebookMessages({
          userId,
          limit,
          direction: 'initial',
        })
      ).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, userId, limit]);

  return {
    messages,
    loading,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    loadOlder,
    loadNewer,
    refresh,
  };
};

export const useInstagramMessagesInfiniteScroll = (userId, limit = 20) => {
  const dispatch = useDispatch();
  const messages = useSelector(selectInstagramMessages);
  const loading = useSelector(selectInstagramMessagesLoading);
  const pagination = useSelector(selectInstagramMessagesPagination);
  const currentUserId = useSelector(selectInstagramMessagesUserId);

  const isFetchingRef = useRef(false);
  const hasInitialLoadRef = useRef(false);
  const userIdRef = useRef(userId);

  useEffect(() => {
    if (userIdRef.current !== userId) {
      userIdRef.current = userId;
      hasInitialLoadRef.current = false;
    }
    if (userId && !hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      isFetchingRef.current = true;
      dispatch(
        getInstagramMessages({
          userId,
          limit,
          direction: 'initial',
        })
      ).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, userId, limit]);

  const loadOlder = useCallback(() => {
    if (
      !loading &&
      !isFetchingRef.current &&
      pagination.hasNextPage &&
      pagination.cursors.after &&
      currentUserId === userId
    ) {
      isFetchingRef.current = true;
      dispatch(
        getInstagramMessages({
          userId,
          limit,
          after: pagination.cursors.after,
          direction: 'prepend',
        })
      ).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, loading, pagination, userId, currentUserId, limit]);

  const loadNewer = useCallback(() => {
    if (
      !loading &&
      !isFetchingRef.current &&
      pagination.hasPreviousPage &&
      pagination.cursors.before &&
      currentUserId === userId
    ) {
      isFetchingRef.current = true;
      dispatch(
        getInstagramMessages({
          userId,
          limit,
          before: pagination.cursors.before,
          direction: 'prepend',
        })
      ).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, loading, pagination, userId, currentUserId, limit]);

  const refresh = useCallback(() => {
    if (userId) {
      isFetchingRef.current = true;
      hasInitialLoadRef.current = true;
      dispatch(
        getInstagramMessages({
          userId,
          limit,
          direction: 'initial',
        })
      ).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, userId, limit]);

  return {
    messages,
    loading,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    loadOlder,
    loadNewer,
    refresh,
  };
};

export const useFacebookConversationsInfiniteScroll = (limit = 10) => {
  const dispatch = useDispatch();
  const conversations = useSelector(selectFacebookConversations);
  const loading = useSelector(selectFacebookConversationsLoading);
  const pagination = useSelector(selectFacebookPagination);

  const isFetchingRef = useRef(false);
  const hasInitialLoadRef = useRef(false);

  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      dispatch(getFacebookConversations({ limit, append: false }));
    }
  }, [dispatch, limit]);

  const loadMore = useCallback(() => {
    if (
      !loading &&
      !isFetchingRef.current &&
      pagination.hasNextPage &&
      pagination.cursors.after
    ) {
      isFetchingRef.current = true;
      dispatch(
        getFacebookConversations({
          limit,
          after: pagination.cursors.after,
          append: true,
        })
      ).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, loading, pagination, limit]);

  const refresh = useCallback(() => {
    isFetchingRef.current = false;
    hasInitialLoadRef.current = true;
    dispatch(getFacebookConversations({ limit, append: false }));
  }, [dispatch, limit]);

  return {
    conversations,
    loading,
    hasNextPage: pagination.hasNextPage,
    loadMore,
    refresh,
  };
};

export const useInstagramConversationsInfiniteScroll = (limit = 10) => {
  const dispatch = useDispatch();
  const conversations = useSelector(selectInstagramConversations);
  const loading = useSelector(selectInstagramConversationsLoading);
  const pagination = useSelector(selectInstagramPagination);

  const isFetchingRef = useRef(false);

  const loadMore = useCallback(() => {
    if (
      !loading &&
      !isFetchingRef.current &&
      pagination.hasNextPage &&
      pagination.cursors.after
    ) {
      isFetchingRef.current = true;
      dispatch(
        getInstagramConversations({
          limit,
          after: pagination.cursors.after,
          append: true,
        })
      ).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, loading, pagination, limit]);

  const refresh = useCallback(() => {
    isFetchingRef.current = true;
    dispatch(getInstagramConversations({ limit })).finally(() => {
      isFetchingRef.current = false;
    });
  }, [dispatch, limit]);

  return {
    conversations,
    loading,
    hasNextPage: pagination.hasNextPage,
    loadMore,
    refresh,
  };
};

export default useFacebookMessagesInfiniteScroll;
